import type { SxProps, Theme } from "@mui/material";
import { type FC, memo, useCallback, useMemo, useState } from "react";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { TEAM_SECTIONS } from "../team/config";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown";

type EmploymentTypeSelectorProps = {
	onSectionsChange?: (selectedSections: Set<string>) => void;
	selectSx?: SxProps<Theme>;
	/** Initial checked sections (defaults to all). Used to restore a persisted selection. */
	initialSelectedSections?: string[];
};

const DEFAULT_SELECT_SX: SxProps<Theme> = { width: "200px" };

const EmploymentTypeSelector: FC<EmploymentTypeSelectorProps> = memo(({ onSectionsChange, selectSx = DEFAULT_SELECT_SX, initialSelectedSections }) => {
	const { track } = useAnalytics();

	const items = useMemo(
		() =>
			TEAM_SECTIONS.map((section) => ({
				label: section.title,
				value: section.title,
			})),
		[],
	);

	const [selectedSections, setSelectedSections] = useState<string[]>(initialSelectedSections ?? items.map((item) => item.value));

	const handleChange = useCallback(
		(values: string[]) => {
			track("employment_type_selector_changed", {
				previous_selection: selectedSections.join(","),
				new_selection: values.join(","),
			});
			onSectionsChange?.(new Set(values));
			setSelectedSections(values);
		},
		[selectedSections, onSectionsChange, track],
	);

	return (
		<SystemDropdown
			label="Contract Type"
			multiple
			items={items}
			selectedItems={selectedSections}
			onChangeMultiple={handleChange}
			selectSx={selectSx}
			renderAllValueText="All"
		/>
	);
});

export default EmploymentTypeSelector;
